import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || '';

class SocketService {
  constructor() {
    this.socket = null;
    this.callHandlers = {};
  }

  connect(token) {
    if (this.socket?.connected) return;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 3000,
      timeout: 10000,
      pingTimeout: 30000,
      pingInterval: 25000,
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connecte:', this.socket.id);
      this._setupCallListeners();
      // Rattrapage : recharger les messages de la conversation active
      try {
        const { useMessageStore } = require('../store');
        const state = useMessageStore.getState();
        if (state.activeConversationId) {
          state.fetchMessages(state.activeConversationId);
        }
      } catch(e) {}
    });

    this.socket.on('disconnect', (reason) => {
      console.warn('WebSocket deconnecte:', reason);
    });

    this.socket.on('connect_error', (err) => {
      console.error('Erreur WebSocket:', err.message);
    });

    // Messages
    this.socket.on('user:status', ({ userId, online }) => {
      const { useMessageStore } = require('../store');
      useMessageStore.getState().setOnlineStatus(userId, online);
    });

    this.socket.on('message:expired', ({ message_id, conversation_id }) => {
      const { useMessageStore } = require('../store');
      useMessageStore.getState().updateMessage(conversation_id, message_id, {
        is_deleted: true,
        content: 'Message expiré',
        type: 'text',
      });
    });

    this.socket.on('message:receipt', ({ message_id, conversation_id, status }) => {
      const { useMessageStore } = require('../store');
      if (status !== 'read') return;
      const state = useMessageStore.getState();
      if (conversation_id && state.messages[conversation_id]) {
        state.messages[conversation_id].forEach(m => {
          if (!m.is_read) state.updateMessage(conversation_id, m.id, { is_read: true });
        });
        state.markConversationRead(conversation_id);
      }
    });

    this.socket.on('new_message', (message) => {
      const { useMessageStore } = require('../store');
      useMessageStore.getState().addMessage(message);
    });

    // Frappe
    this.socket.on('user:typing', ({ userId, name, conversation_id }) => {
      const { useMessageStore } = require('../store');
      useMessageStore.getState().setTyping(conversation_id, userId, name, true);
    });

    this.socket.on('user:stop_typing', ({ userId, conversation_id }) => {
      const { useMessageStore } = require('../store');
      useMessageStore.getState().setTyping(conversation_id, userId, null, false);
    });

    this._setupCallListeners();
  }

  _setupCallListeners() {
    if (!this.socket) return;
    this.socket.off('call:incoming');
    this.socket.off('call:ended');
    this.socket.off('call:rejected');
    this.socket.off('call:accepted');
    this.socket.off('call:signal');
    this.socket.off('call:ringing');
    this.socket.off('call:unavailable');
    this.socket.on('call:incoming', (data) => {
      console.log('CALL INCOMING RECU:', data);
      if (this.callHandlers.onIncoming) this.callHandlers.onIncoming(data);
    });

    this.socket.on('call:ended', (data) => {
      if (this.callHandlers.onEnded) this.callHandlers.onEnded(data);
    });

    this.socket.on('call:rejected', (data) => {
      if (this.callHandlers.onRejected) this.callHandlers.onRejected(data);
    });

    this.socket.on('call:accepted', (data) => {
      if (this.callHandlers.onAccepted) this.callHandlers.onAccepted(data);
    });

    this.socket.on('call:signal', (data) => {
      if (this.callHandlers.onSignal) this.callHandlers.onSignal(data);
    });

    this.socket.on('call:ringing', (data) => {
      if (this.callHandlers.onRinging) this.callHandlers.onRinging(data);
    });

    this.socket.on('call:unavailable', (data) => {
      if (this.callHandlers.onUnavailable) this.callHandlers.onUnavailable(data);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Messages
  joinConversation(conversationId) {
    this.socket?.emit('conversation:join', { conversation_id: conversationId });
  }

  sendMessage(conversationId, content, callback, type, expiresAt) {
    this.socket?.emit('message:send', { 
      conversation_id: conversationId, 
      content, 
      type: type || 'text',
      expires_at: expiresAt || null
    }, callback);
  }

  sendTypingStart(conversationId) {
    this.socket?.emit('typing:start', { conversation_id: conversationId });
    // Auto-stop après 4 secondes
    if (this._typingTimer) clearTimeout(this._typingTimer);
    this._typingTimer = setTimeout(() => {
      this.socket?.emit('typing:stop', { conversation_id: conversationId });
    }, 4000);
  }

  sendTypingStop(conversationId) {
    this.socket?.emit('typing:stop', { conversation_id: conversationId });
  }

  markRead(conversationId, messageId) {
    this.socket?.emit('message:read', { conversation_id: conversationId, message_id: messageId });
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  // Appels
  initiateCall(targetUserId, conversationId, callType) {
    this.socket?.emit('call:initiate', { targetUserId, conversationId, callType });
  }

  initiateGroupCall(conversationId, callType) {
    if (this.socket) {
      this.socket.emit('call:initiate-group', { conversationId, callType });
      return true;
    }
    return false;
  }

  acceptCall(callId) {
    this.socket?.emit('call:accept', { callId });
  }

  rejectCall(callId) {
    this.socket?.emit('call:reject', { callId });
  }

  endCall(callId) {
    this.socket?.emit('call:end', { callId });
  }

  sendCallSignal(callId, targetUserId, signal) {
    this.socket?.emit('call:signal', { callId, targetUserId, signal });
  }

  // Enregistrer les handlers - stockés dans callHandlers (objet partagé par référence)
  setCallHandlers(handlers) {
    Object.assign(this.callHandlers, handlers);
    this._setupCallListeners();
    console.log('Call handlers enregistres:', Object.keys(this.callHandlers));
  }

  onCallEvent(event, callback) {
    this.socket?.on(event, callback);
  }

  offCallEvent(event) {
    this.socket?.off(event);
  }
}

export const socketService = new SocketService();
export default socketService;
